import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function CookiesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [managed, setManaged] = useState(false);

  const handleResetConsent = () => {
    localStorage.removeItem("ew_cookie_consent");
    localStorage.removeItem("ew_cookie_consent_date");
    setManaged(true);
    setTimeout(() => window.location.reload(), 1000);
  };

  const cookieCategories = [
    {
      name: "Strictly Necessary",
      icon: "ri-shield-keyhole-line",
      color: "green",
      required: true,
      desc: "These cookies are essential for the website to function correctly. They enable core functionality such as page navigation and access to secure areas.",
      examples: [
        { name: "ew_cookie_consent", purpose: "Stores your cookie consent preferences", duration: "1 year" },
        { name: "ew_session", purpose: "Maintains your session whilst browsing", duration: "Session" },
      ],
    },
    {
      name: "Analytics",
      icon: "ri-bar-chart-line",
      color: "amber",
      required: false,
      desc: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the site.",
      examples: [
        { name: "_ga", purpose: "Google Analytics — distinguishes users", duration: "2 years" },
        { name: "_gid", purpose: "Google Analytics — distinguishes users", duration: "24 hours" },
        { name: "_gat", purpose: "Google Analytics — throttles request rate", duration: "1 minute" },
      ],
    },
    {
      name: "Marketing",
      icon: "ri-megaphone-line",
      color: "orange",
      required: false,
      desc: "These cookies are used to deliver adverts more relevant to you and your interests. They may also be used to limit the number of times you see an advertisement.",
      examples: [
        { name: "_fbp", purpose: "Facebook Pixel — tracks conversions", duration: "3 months" },
        { name: "ads/ga-audiences", purpose: "Google Ads remarketing", duration: "Session" },
      ],
    },
    {
      name: "Preferences",
      icon: "ri-settings-3-line",
      color: "stone",
      required: false,
      desc: "These cookies enable us to remember information that changes the way the website behaves or looks, such as your preferred language or the region you are in.",
      examples: [
        { name: "ew_prefs", purpose: "Stores your display preferences", duration: "6 months" },
      ],
    },
  ];

  const colorMap: Record<string, string> = {
    green: "bg-green-100 text-green-600",
    amber: "bg-amber-100 text-amber-600",
    orange: "bg-orange-100 text-orange-600",
    stone: "bg-stone-200 text-stone-600",
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="bg-[#1C2B1F] pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">Legal</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Cookie Policy &amp; GDPR</h1>
          <p className="text-stone-300 text-base">Last updated: 15 April 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">

          {/* Intro */}
          <div>
            <p className="text-zinc-600 text-sm bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md">
              This Cookie Policy explains what cookies are, how Ensure Windows uses them, and how you can manage your cookie preferences. We are committed to complying with the UK GDPR, the Privacy and Electronic Communications Regulations (PECR), and the Data Protection Act 2018.
            </p>
          </div>

          {/* What are cookies */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">1</span>
              What Are Cookies?
            </h2>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and supply information to the website owners. Cookies do not contain personal information in themselves but may be linked to personal data you have provided.
            </p>
          </div>

          {/* Cookie categories */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-6 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">2</span>
              Cookies We Use
            </h2>
            <div className="space-y-6">
              {cookieCategories.map((cat) => (
                <div key={cat.name} className="border border-stone-200 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-4 p-5 bg-stone-50 border-b border-stone-200">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${colorMap[cat.color]}`}>
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-zinc-900 text-sm">{cat.name}</h3>
                        {cat.required && (
                          <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Always Active</span>
                        )}
                        {!cat.required && (
                          <span className="text-xs font-semibold bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full">Optional</span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-xs mt-0.5">{cat.desc}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-stone-100 border-b border-stone-200">
                          <th className="text-left px-5 py-2 font-semibold text-zinc-700">Cookie Name</th>
                          <th className="text-left px-5 py-2 font-semibold text-zinc-700">Purpose</th>
                          <th className="text-left px-5 py-2 font-semibold text-zinc-700">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.examples.map((ex) => (
                          <tr key={ex.name} className="border-t border-stone-100">
                            <td className="px-5 py-3 font-mono text-zinc-700 font-medium">{ex.name}</td>
                            <td className="px-5 py-3 text-zinc-600">{ex.purpose}</td>
                            <td className="px-5 py-3 text-zinc-500">{ex.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* GDPR Basis */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">3</span>
              GDPR Legal Basis for Cookies
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                <strong className="text-zinc-900 text-sm block mb-1">Necessary Cookies</strong>
                <p className="text-zinc-600 text-sm">Processed under <strong>Legitimate Interests</strong> — essential for the operation of the website.</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
                <strong className="text-zinc-900 text-sm block mb-1">Optional Cookies</strong>
                <p className="text-zinc-600 text-sm">Processed under <strong>Consent</strong> (Article 6(1)(a) UK GDPR) — only placed after you have given explicit consent.</p>
              </div>
            </div>
          </div>

          {/* Your rights */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">4</span>
              Managing Your Cookie Preferences
            </h2>
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              You can manage your cookie preferences at any time. You can withdraw consent or change your selections using the button below. You can also control cookies through your browser settings — note that disabling all cookies may affect site functionality.
            </p>
            {managed ? (
              <p className="text-green-700 font-medium text-sm bg-green-50 border border-green-200 p-3 rounded-lg">
                Preferences reset. The page will reload shortly&hellip;
              </p>
            ) : (
              <button
                onClick={handleResetConsent}
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Manage My Cookie Preferences
              </button>
            )}
          </div>

          {/* Third party */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">5</span>
              Third-Party Cookies
            </h2>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. Please refer to the respective third-party privacy policies: <a href="https://policies.google.com/privacy" rel="nofollow" target="_blank" className="text-amber-600 hover:underline">Google</a>, <a href="https://www.facebook.com/policy/cookies" rel="nofollow" target="_blank" className="text-amber-600 hover:underline">Meta/Facebook</a>.
            </p>
          </div>

          {/* Changes */}
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-[#1C2B1F] text-amber-400 rounded-full text-sm font-bold">6</span>
              Changes to This Policy
            </h2>
            <p className="text-zinc-600 text-sm leading-relaxed">
              We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date. Continued use of the website after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-[#1C2B1F] text-white rounded-xl p-6">
            <h3 className="font-bold text-lg mb-2">Questions About Cookies?</h3>
            <p className="text-stone-300 text-sm mb-4">
              If you have any questions about our use of cookies or your data rights, please contact us. You also have the right to complain to the{" "}
              <a href="https://ico.org.uk" rel="nofollow" target="_blank" className="text-amber-400 hover:underline">Information Commissioner&apos;s Office (ICO)</a>.
            </p>
            <div className="flex flex-wrap gap-5">
              <a href="mailto:sales@ensurewindows.co.uk" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line"></i></div>
                sales@ensurewindows.co.uk
              </a>
              <a href="tel:07852636300" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line"></i></div>
                07852 636 300
              </a>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4">
              <Link to="/privacy-policy" className="text-stone-300 hover:text-amber-400 text-sm cursor-pointer">Privacy Policy</Link>
              <Link to="/terms-conditions" className="text-stone-300 hover:text-amber-400 text-sm cursor-pointer">Terms &amp; Conditions</Link>
              <Link to="/cancellation-policy" className="text-stone-300 hover:text-amber-400 text-sm cursor-pointer">Cancellation Policy</Link>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
